import {
	forwardRef,
	ReactElement,
	Ref,
	RefObject,
	useEffect,
	useImperativeHandle,
	useRef,
	useState
} from "react";
import classNames from "classnames";
import { SizeInformation } from "client_legacy/ndaw/sizing";
import styles from "./HtmlInFrame.module.less";
import { isNotNil, debounce } from "client/Misc/utils";
import { setRef } from "client/Misc/reactUtils";
import { useForceRerender, useValueEqualMemo } from "client/Hooks";
import { IFrame, IFramePermission } from "../IFrame/IFrame";
import { isSafari } from "client_legacy/ndaw/mobile";

const LAYOUT = (
	head: string,
	body: string,
	size: HtmlInFrameSize
): string => `<!DOCTYPE html>
<html>
<head>
	<meta http-equiv="X-UA-Compatible" content="IE=edge">
	<style>
		html,
		body {
			width: ${size.horizontal === "content-size" ? "max-content" : "100%"};
			height: ${size.vertical === "content-size" ? "max-content" : "100%"};
		}

		html {
			overflow: hidden;
		}

		body {
			margin: 0 ${size.horizontal === "stretch" ? "auto" : "0"};
			overflow-x: ${size.horizontal === "content-size" ? "hidden" : "auto"};
			overflow-y: ${size.vertical === "content-size" ? "hidden" : "auto"};
			-webkit-overflow-scrolling: touch;
		}

		body > iframe {
			display: block;
			border: none;
		}

		${size.horizontal === "stretch"
			? `
				body > iframe:not([width]) {
					width: 100%;
				}
			`
			: ""
		}

		${size.vertical === "stretch"
			? `
				body > iframe:not([height]) {
					height: 100%;
				}
			`
			: ""
		}

	</style>
	${head}
</head>
<body>
<div>
${body}
</div>
</body>
</html>`;

export type HtmlInFrameSizeKind = "stretch" | "content-size";

export type HtmlInFrameSize = {
	horizontal: HtmlInFrameSizeKind;
	vertical: HtmlInFrameSizeKind;
};

export type HtmlInFrameProps = {
	body: string;
	head?: string;
	size?: HtmlInFrameSizeKind | HtmlInFrameSize;
	frameWindowRef?: Ref<Window | null>;
	trackingId: string;
};

export type HtmlInFrameHandle = {
	reInitializeFrame(): void;
};

export const HtmlInFrame = forwardRef(
	(props: HtmlInFrameProps, forwardedRef: Ref<HtmlInFrameHandle>): ReactElement => {
		const {
			body,
			head = "",
			size = "content-size",
			frameWindowRef,
			trackingId
		} = props;

		const frameRef: RefObject<HTMLIFrameElement | null> = useRef<HTMLIFrameElement>(null);
		const [contentSize, setContentSize] = useState<Partial<SizeInformation>>({});
		const [reInitializeFrame, frameVersion] = useForceRerender();

		const normalizedSize: HtmlInFrameSize = useValueEqualMemo(
			(): HtmlInFrameSize => {
				return typeof size === "string"
				? {
					horizontal: size,
					vertical: size
				}
				: size;
			},
			[size]
		);

		useEffect(
			() => {
				const eventRegistrationCleanUpCallbacks: (() => void)[] = [];
				let iframeLoadCheckInterval: SetIntervalHandle | undefined;

				if (!frameRef.current?.contentWindow) {
					return;
				}

				const frameWindow: Window = frameRef.current
					.contentWindow;

				const iframeDocument: Document = frameWindow.document;
				iframeDocument.open();
				// NOTE frameWindowRef must be set in this precise position, because open removes event listeners
				// and they should be reattached before we write any content
				if (isNotNil(frameWindowRef)) {
					setRef(frameWindowRef, frameWindow);
				}

				iframeDocument.write(
					LAYOUT(
						head,
						body,
						normalizedSize
					)
				);
				iframeDocument.close();

				const updateContentSize = (): void => {
					setContentSize({
						width: iframeDocument.body
							.offsetWidth,
						height: iframeDocument.body
							.offsetHeight
					});
				};

				const debouncedUpdateContentSize: () => void = debounce(updateContentSize, 50);

				const windowLoadHandler = (): void => {
					debouncedUpdateContentSize();
					const allImages: NodeListOf<HTMLImageElement> = iframeDocument.querySelectorAll("img");
					allImages.forEach(
						image => {
							eventRegistrationCleanUpCallbacks.push(
								subscribeToEvent(image, "load", debouncedUpdateContentSize)
							);
						}
					);
				};

				if (isSafari()) {
					// Safari doesn't fire load event
					// for iframes without a valid src
					iframeLoadCheckInterval = setInterval(
						(): void => {
							try {
								if (iframeDocument.readyState === "complete"
									|| iframeDocument.readyState === "interactive"
								) {
									windowLoadHandler();
									clearInterval(iframeLoadCheckInterval);
								}
							} catch (e) {
								console.error(e);
								clearInterval(iframeLoadCheckInterval);
							}
						},
						100
					);
				} else {
					eventRegistrationCleanUpCallbacks.push(
						subscribeToEvent(frameWindow, "load", windowLoadHandler)
					);
				}

				eventRegistrationCleanUpCallbacks.push(
					subscribeToEvent(
						window,
						"resize",
						debouncedUpdateContentSize
					)
				);

				updateContentSize();

				return () => {
					eventRegistrationCleanUpCallbacks.forEach(
						cleanUp => cleanUp()
					);

					if (isNotNil(iframeLoadCheckInterval)) {
						clearInterval(iframeLoadCheckInterval);
					}
				};
			},
			[
				body,
				frameWindowRef,
				head,
				normalizedSize,
				frameVersion
			]
		);

		useImperativeHandle(
			forwardedRef,
			(): HtmlInFrameHandle => ({
				reInitializeFrame
			}),
			[reInitializeFrame]
		);

		return (
			<IFrame
				key={frameVersion}
				ref={frameRef}
				style={{
					width: normalizedSize.horizontal === "content-size"
						? contentSize.width
						: undefined,
					height: normalizedSize.vertical === "content-size"
						? contentSize.height
						: undefined
				}}
				className={classNames(
					{
						[styles.root_stretchedX]: normalizedSize.horizontal === "stretch",
						[styles.root_stretchedY]: normalizedSize.vertical === "stretch"
					}
				)}
				trackingId={trackingId}
				permissions={iframePermissions}
			/>
		);
	}
);

HtmlInFrame.displayName = "HtmlInFrame";

function subscribeToEvent(
	target: EventTarget,
	type: string,
	callback: () => void
): (() => void) {
	target.addEventListener(type, callback);

	return () => target.removeEventListener(type, callback);
}

const iframePermissions: readonly IFramePermission[] = [
	IFramePermission.Downloads,
	IFramePermission.Forms,
	IFramePermission.Modals,
	IFramePermission.OrientationLock,
	IFramePermission.PointerLock,
	IFramePermission.Popups,
	IFramePermission.EscapingPopups,
	IFramePermission.Presentation,
	IFramePermission.SameOrigin,
	IFramePermission.Scripts
];